import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	DrawerAutoDataList,
	IDrawerDataListColumn,
	Loading,
	useCrudEntity,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { PermissionDto, RoleCreateDto, RoleListDto, RoleUpdateDto } from "apiGen";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IRoleFormState, RoleCreateDrawer, RoleUpdateDrawer } from "~root/roles/RoleDrawer";
import { permissionService, roleService } from "~shared/api/roles/roleService";
import { timesVisibilityOptionNames, vacationsVisibilityOptionNames } from "./permissions";

const mapToRequest = (values: IRoleFormState): RoleCreateDto | RoleUpdateDto => {
	return {
		name: values.name,
		vacationsVisibilityId: values.vacationsVisibility.id,
		timesVisibilityId: values.timesVisibility.id,
		payrollsVisibilityId: (values.payrollsVisibility as PermissionDto).id,
		permissions: Object.keys(values.permissions)
			.filter((x) => {
				if ([...vacationsVisibilityOptionNames, ...timesVisibilityOptionNames].includes(x)) return false;
				return values.permissions[x].enabled;
			})
			.map((x) => values.permissions[x].id as number),
	};
};
export const RoleListPage = () => {
	const { t } = useTranslation();
	const crudOperations = useCrudEntity(roleService, {
		entityName: t("Role"),
		messages: {
			create: t("Role was saved."),
			update: t("Role was saved."),
			delete: t("Role was deleted."),
		},
	});
	const dataListColumns: IDrawerDataListColumn<RoleListDto>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Users"),
				valueKey: "users",
				render: ({ users }) => users.map((x) => x.email).join(", "),
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit role")}
							label={t("Edit role")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete role")}
							label={t("Delete role")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this role?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);
	const { data: availablePermissions = [], isLoading } = useQuery({
		queryKey: permissionService.indexKey,
		queryFn: () => permissionService.index().then((x) => x.data.data),
	});

	if (isLoading) {
		return <Loading />;
	}

	return (
		<DrawerAutoDataList
			canCreate
			canUpdate
			columns={dataListColumns}
			service={roleService}
			renderCreateDrawer={(drawerProps) => (
				<RoleCreateDrawer
					{...drawerProps}
					availablePermissions={availablePermissions}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<RoleUpdateDrawer
					{...drawerProps}
					availablePermissions={availablePermissions}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			translations={{
				title: t("Roles"),
				empty: t("No role available."),
			}}
		/>
	);
};
